import * as React from "react";

import { definedMap } from "@glideapps/ts-necessities";

import type { GlideFunctionalRenderer } from "common/components/uis";
import { LOADING } from "common/components/computation-types";
import {
    asArrayValue,
    isLoadedDefined,
    asString,
    asNumber,
    loadedDefinedMap,
    asBoolean,
} from "common/components/component-helpers";
import type { PalletteType } from "common/generator/components/color-types";
import { mapFilterUndefined } from "common/Support";
import { logInfo } from "common/debug-print";
import type { ChartDatum } from "../ui/smart/charts/minimal-chart/types";
import { makeChartDatumFromValue } from "./pie-chart-material-renderer";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { ErrorBoundary } from "lib/error-boundary";

const BarChart = lazilyLoadedComponent("bar-chart", () => import("../ui/smart/charts/bar-chart/bar-chart"));

export const BarChartMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [list, titleValue, captionValue, showLegendValue, showLabelsValue, seriesLabelsValue, colorValue],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    if (list === LOADING) return env.renderProgress();
    if (list === undefined) return null;

    const title = loadedDefinedMap(titleValue, asString);
    const caption = loadedDefinedMap(captionValue, asString);
    const showLabelsOnBars = loadedDefinedMap(showLabelsValue, asBoolean);
    const showLegendByDefault = loadedDefinedMap(showLegendValue, asBoolean);
    const pallette: PalletteType = loadedDefinedMap(colorValue, asString) as PalletteType;
    const seriesLabels = loadedDefinedMap(seriesLabelsValue, s => asArrayValue(s).map(l => asString(force(l))));

    logInfo("series labels", seriesLabels);

    const data = mapFilterUndefined(asArrayValue(list), objectVP => {
        const inspector = objectVP.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) return undefined;

        const label = definedMap(inspector.getProperty("label"), force);
        if (!isLoadedDefined(label)) return undefined;

        let values: readonly ChartDatum[];
        if (inspector.getProperty("values") !== undefined) {
            const valuesArr = loadedDefinedMap(definedMap(inspector.getProperty("values"), force), asArrayValue);
            if (valuesArr === undefined) return undefined;

            values = mapFilterUndefined(
                valuesArr,
                vp =>
                    loadedDefinedMap(force(vp), v => makeChartDatumFromValue(v, force)) ?? {
                        value: 0,
                        displayValue: undefined,
                    }
            );
        } else if (inspector.getProperty("value") !== undefined) {
            const value = loadedDefinedMap(definedMap(inspector.getProperty("value"), force), asNumber);
            if (value === undefined) return undefined;

            values = [{ value: asNumber(value), displayValue: undefined }];
        } else {
            return undefined;
        }

        return {
            values,
            title: asString(label),
        };
    });

    if (data.length === 0) return null;

    let isSingleSeries = false;

    if (data[0].values.length === 1) {
        isSingleSeries = true;
    }

    return (
        <ErrorBoundary>
            <React.Suspense fallback={<div style={{ width: 240, height: 240 }} />}>
                <BarChart
                    title={title}
                    caption={caption}
                    data={data}
                    seriesLabels={seriesLabels || []}
                    isSingleSeries={isSingleSeries}
                    pallette={pallette}
                    showLegendByDefault={showLegendByDefault}
                    showLabelsOnBars={showLabelsOnBars}
                />
            </React.Suspense>
        </ErrorBoundary>
    );
};
